/**
 * components/client-detail/ClientInfoTab.tsx
 *
 * CRM-style information tab for the client detail page.
 * Displays all collected responses in a single unified card, merging fields
 * across all form submissions. Uses short_label (when set) as the field display
 * name, falling back to label. Falls back to label for fields without a short_label.
 * When the same field appears in multiple forms, the most recent value is kept
 * (formGroups are ordered newest-first from the parent).
 * Updated Mar 2026: merged all form groups into one unified card; fields deduplicated
 *   by shortLabel ?? label, grouped by section.
 * Updated Apr 2026: added completedAt to FormGroup; buildValueHistoryMap collects
 *   all distinct previous values per field across re-submissions and surfaces them via
 *   PreviousValuePopover (neutral/discreet style, multi-entry).
 * Fix Apr 2026: sort formGroups by completedAt DESC locally (upstream orders by
 *   created_at which can differ); normalizeEmpty maps null/'' to null so that
 *   empty-to-empty transitions do not spuriously appear in history.
 * Updated Apr 2026: added embedded prop — when true the outer card wrapper is
 *   suppressed so the parent container provides the border/shadow.
 * Updated Jun 2026: replaced catalogName with shortLabel (short_label from DB).
 *   Admin display: shortLabel ?? label.
 */

import { AlignLeft, Minus } from 'lucide-react';
import { getFieldTypeMeta } from '../../utils/fieldTypeMeta';
import type { FieldType } from '../../types/database';
import PreviousValuePopover from '../clients/PreviousValuePopover';

interface FieldResponse {
  fieldId: string;
  label: string;
  shortLabel: string | null;
  type: FieldType;
  value: string | null;
  section: string | null;
}

interface FormGroup {
  requestId: string;
  completedAt: string | null;
  fields: FieldResponse[];
}

interface ClientInfoTabProps {
  formGroups: FormGroup[];
  embedded?: boolean;
}

type HistoryEntry = { value: string | null; submittedAt: string | null };

/** Treats null and '' as the same canonical "empty" value (null). */
function normalizeEmpty(value: string | null | undefined): string | null {
  return value === null || value === undefined || value === '' ? null : value;
}

/** Returns a copy of formGroups sorted by completedAt descending (newest first). */
function sortByCompletedAtDesc(groups: FormGroup[]): FormGroup[] {
  return [...groups].sort((a, b) => {
    if (!a.completedAt && !b.completedAt) return 0;
    if (!a.completedAt) return 1;
    if (!b.completedAt) return -1;
    return b.completedAt.localeCompare(a.completedAt);
  });
}

/**
 * Builds a map of fieldKey → ordered history of previous distinct values (newest-first).
 * formGroups must be ordered newest-first. Only the first occurrence of each key is the
 * current value; subsequent occurrences are historical. Consecutive identical values in the
 * chain are deduplicated (e.g. A→A→B records only B, not two As).
 * null and '' are treated as equivalent ("empty") to avoid spurious history entries.
 */
function buildValueHistoryMap(formGroups: FormGroup[]): Map<string, HistoryEntry[]> {
  const currentValue = new Map<string, string | null>();
  const historyMap = new Map<string, HistoryEntry[]>();

  for (const group of formGroups) {
    for (const field of group.fields) {
      if (field.type === 'document') continue;
      const key = field.shortLabel ?? field.label;
      const normalized = normalizeEmpty(field.value);

      if (!currentValue.has(key)) {
        currentValue.set(key, normalized);
      } else {
        const entries = historyMap.get(key) ?? [];
        const lastKnown = entries.length === 0
          ? currentValue.get(key)!
          : entries[entries.length - 1].value;
        if (normalized !== lastKnown) {
          entries.push({ value: normalized, submittedAt: group.completedAt });
          historyMap.set(key, entries);
        }
      }
    }
  }

  return historyMap;
}

function buildMergedSections(formGroups: FormGroup[]): [string, FieldResponse[]][] {
  const seen = new Set<string>();
  const merged: FieldResponse[] = [];

  for (const group of formGroups) {
    for (const field of group.fields) {
      if (field.type === 'document') continue;
      const key = field.shortLabel ?? field.label;
      if (!seen.has(key)) {
        seen.add(key);
        merged.push(field);
      }
    }
  }

  const sectionMap = merged.reduce<Record<string, FieldResponse[]>>((acc, field) => {
    const key = field.section || '__default__';
    if (!acc[key]) acc[key] = [];
    acc[key].push(field);
    return acc;
  }, {});

  return Object.entries(sectionMap);
}

function FieldCard({
  field,
  history,
}: {
  field: FieldResponse;
  history?: HistoryEntry[];
}) {
  const hasValue = field.value !== null && field.value !== undefined && field.value !== '';

  return (
    <div className="flex flex-col gap-1">
      {/* Label */}
      <div className="flex items-center gap-1.5 text-[10px] font-semibold text-neutral-400 uppercase tracking-[0.08em]">
        <span>{getFieldTypeMeta(field.type).icon('xs')}</span>
        <span>{field.shortLabel ?? field.label}</span>
      </div>
      {/* Value — no box, pure typography */}
      {hasValue ? (
        <p className="text-sm text-neutral-900 leading-relaxed">
          {field.value}
        </p>
      ) : (
        <div className="flex items-center gap-1">
          <Minus className="w-3 h-3 text-neutral-300" />
          <span className="text-sm text-neutral-300 italic">Non renseigné</span>
        </div>
      )}
      {history && history.length > 0 && (
        <PreviousValuePopover variant="resubmission" history={history} />
      )}
    </div>
  );
}

export default function ClientInfoTab({ formGroups, embedded = false }: ClientInfoTabProps) {
  const hasAnyInfoField = formGroups.some(g =>
    g.fields.some(f => f.type !== 'document')
  );

  if (!hasAnyInfoField) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-neutral-100 rounded-2xl mb-4">
          <AlignLeft className="w-7 h-7 text-neutral-400" />
        </div>
        <p className="text-neutral-600 font-medium mb-1">Aucune information collectée</p>
        <p className="text-sm text-neutral-400">
          Les informations renseignées par le client apparaîtront ici
        </p>
      </div>
    );
  }

  const sorted = sortByCompletedAtDesc(formGroups);
  const sections = buildMergedSections(sorted);
  const valueHistoryMap = buildValueHistoryMap(sorted);

  const inner = (
    <>
      {sections.map(([sectionKey, fields], sectionIdx) => (
        <div key={sectionKey}>
          {sectionKey !== '__default__' && (
            <div className="px-6 py-3 bg-neutral-50 border-b border-neutral-100">
              <span className="text-xs font-semibold text-neutral-500 uppercase tracking-wider">
                {sectionKey}
              </span>
            </div>
          )}
          <div className="divide-y divide-neutral-100">
            {Array.from({ length: Math.ceil(fields.length / 2) }, (_, rowIdx) => {
              const left = fields[rowIdx * 2];
              const right = fields[rowIdx * 2 + 1];
              return (
                <div key={left.fieldId} className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-neutral-100">
                  <div className="px-6 py-4">
                    <FieldCard
                      field={left}
                      history={valueHistoryMap.get(left.shortLabel ?? left.label)}
                    />
                  </div>
                  {right && (
                    <div className="px-6 py-4">
                      <FieldCard
                        field={right}
                        history={valueHistoryMap.get(right.shortLabel ?? right.label)}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {sectionIdx < sections.length - 1 && (
            <div className="border-t border-neutral-200" />
          )}
        </div>
      ))}
    </>
  );

  if (embedded) return <>{inner}</>;

  return (
    <div className="bg-white rounded-2xl border border-neutral-200 overflow-hidden shadow-soft">
      {inner}
    </div>
  );
}

export type { FormGroup, FieldResponse };
